import { ReducerCreators } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import { PasscodeListsState } from './types';
import {
  GetAssociatedEventDetails,
  GetAssociatedEvents,
  GetMemberPasscodeLists,
  GetPasscodeList,
  GetPasscodeListMemberCount,
  GetPasscodeListMembersForProject,
  GetPasscodeLists,
} from '@/connection/audiences/passcode-lists';

export const reducers = (create: ReducerCreators<PasscodeListsState>) => ({
  fetchPasscodeList: create.asyncThunk(
    async ({ channel, passcodeListId }: { channel: number; passcodeListId: number }) => {
      return await GetPasscodeList(channel, passcodeListId);
    },
    {
      options: {
        condition: ({ passcodeListId }, { getState }) => {
          const state = getState() as RootState;
          return state.passcodeLists.loadingPasscodeListId !== passcodeListId;
        },
      },
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeList = action.payload || [];
        }
        state.loadingPasscodeList = false;
      },
      pending: (state, action) => {
        state.loadingPasscodeList = true;
        state.loadingPasscodeListId = action.meta.arg.passcodeListId;
      },
      settled: (state) => {
        state.loadingPasscodeListId = undefined;
      },
      rejected: (state, action) => {
        state.loadingPasscodeList = false;
        console.error(action.error);
      },
    }
  ),
  fetchPasscodeLists: create.asyncThunk(
    async (channel: number) => {
      return await GetPasscodeLists(channel);
    },
    {
      options: {
        condition: (_, { getState }) => {
          const state = getState() as RootState;
          return !state.passcodeLists.loadingPasscodeLists;
        },
      },
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeLists = action.payload || [];
        }
        state.loadingPasscodeLists = false;
      },
      pending: (state) => {
        state.loadingPasscodeLists = true;
      },
      rejected: (state, action) => {
        state.loadingPasscodeLists = false;
        console.error(action.error);
      },
    }
  ),
  fetchPasscodeListMembers: create.asyncThunk(
    async ({
      channel,
      projectId,
      projectUuid,
      passcodeListId,
      offset,
      limit,
      search,
      sort,
      lists,
    }: {
      channel: number;
      projectId: number;
      projectUuid: string;
      passcodeListId: number;
      offset: number;
      limit: number;
      search: string;
      sort: string;
      lists: string;
    }) => {
      const passcodeListMemberPage = await GetPasscodeListMembersForProject({
        channel,
        projectId,
        projectUuid,
        passcodeListId,
        offset,
        limit,
        sort,
        search,
        lists,
      });
      return passcodeListMemberPage;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListMembers = action.payload || [];
        }
        state.loadingPasscodeListMembers = false;
      },
      pending: (state) => {
        state.loadingPasscodeListMembers = true;
      },
      rejected: (state, action) => {
        state.loadingPasscodeListMembers = false;
        console.error(action.error);
      },
    }
  ),
  resetPasscodeListAndMembers: create.reducer((state) => {
    if (state.passcodeListMembers.length) {
      state.passcodeList = null;
      state.passcodeListMembers = [];
    }
  }),
  fetchPasscodeListMemberCount: create.asyncThunk(
    async ({ passcodeListId, search }: { passcodeListId: number; search: string }) => {
      const passcodeListMemberCount = await GetPasscodeListMemberCount(passcodeListId, search);
      return passcodeListMemberCount;
    },
    {
      options: {
        condition: (_, { getState }) => {
          const state = getState() as RootState;
          return !state.passcodeLists.loadingPasscodeListMemberCount;
        },
      },
      pending: (state) => {
        state.loadingPasscodeListMemberCount = true;
      },
      settled: (state) => {
        state.loadingPasscodeListMemberCount = false;
      },
      fulfilled: (state, action) => {
        if (action.payload && action.payload.length) {
          state.passcodeListMemberCount = action.payload[0]?.member_count || 0;
        }
      },
    }
  ),
  fetchPasscodeListAssociatedEvents: create.asyncThunk(
    async ({ passcodeListId, channel }: { passcodeListId: number; channel: number }) => {
      const passcodeListAssociatedEvents = await GetAssociatedEvents(passcodeListId, channel);
      return passcodeListAssociatedEvents;
    },
    {
      options: {
        condition: ({ passcodeListId }, { getState }) => {
          const state = getState() as RootState;
          return !state.passcodeLists.loadingPasscodeListAssocEvents.includes(passcodeListId);
        },
      },
      pending: (state, action) => {
        state.loadingPasscodeListAssocEvents.push(action.meta.arg.passcodeListId);
      },
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListAssociatedEvents = action.payload || [];
        }
      },
      settled: (state, action) => {
        state.loadingPasscodeListAssocEvents = state.loadingPasscodeListAssocEvents.filter(
          (id) => id !== action.meta.arg.passcodeListId
        );
      },
    }
  ),
  fetchPasscodeListAssociatedEventsDetails: create.asyncThunk(
    async ({ eventList, channel }: { eventList: string[]; channel: number }) => {
      const passcodeListAssociatedEventsDetails = await GetAssociatedEventDetails(eventList, channel);
      return passcodeListAssociatedEventsDetails;
    },
    {
      options: {
        condition: ({ eventList }, { getState }) => {
          const state = getState() as RootState;
          return !state.passcodeLists.loadingPasscodeListAssocEventsDetails.includes(eventList.join(','));
        },
      },
      pending: (state, action) => {
        state.loadingPasscodeListAssocEventsDetails.push(action.meta.arg.eventList.join(','));
      },
      settled: (state, action) => {
        const id = action.meta.arg.eventList.join(',');
        state.loadingPasscodeListAssocEventsDetails = state.loadingPasscodeListAssocEventsDetails.filter(
          (rid) => rid !== id
        );
      },
      fulfilled: (state, action) => {
        if (action.payload) {
          state.passcodeListAssociatedEventsDetails = action.payload || [];
        }
      },
    }
  ),
  fetchMemberPasscodeLists: create.asyncThunk(
    async ({ channel, emails }: { channel: number; emails: string[] }) => {
      const memberPasscodeLists = await GetMemberPasscodeLists(channel, emails);
      return memberPasscodeLists;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          const mapped = action.payload.reduce(
            (acc, item) => {
              acc[item.email] = item.passcode_lists;
              return acc;
            },
            {} as { [key: string]: number[] }
          );
          state.memberPasscodeLists = mapped || {};
          state.loadingMemberPasscodeLists = false;
        }
      },
      pending: (state) => {
        state.loadingMemberPasscodeLists = true;
      },
      rejected: (state, action) => {
        state.loadingMemberPasscodeLists = false;
        console.error(action.error);
      },
    }
  ),
  fetchMagicLinkRegistrationsPasscodeList: create.asyncThunk(
    async ({ channel, passcodeListId }: { channel: number; passcodeListId: number }) => {
      const magicLinkRegistrationsPasscodeList = GetPasscodeList(channel, passcodeListId);
      return magicLinkRegistrationsPasscodeList;
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.magicLinkRegistrationsPasscodeList = action.payload || null;
        }
      },
    }
  ),
});
